import { useTheme } from '../../../theme';
import { type JSX } from 'react';
import Box from '@mui/material/Box';
import type { AvatarProperties, AvatarVariant } from './avatar.types';
import { InitialsAvatar } from './variants/initialsAvatar';
import { ScreenshareAvatar } from './variants/screenshareAvatar';
import { IconDefaultAvatar } from './variants/iconDefaultAvatar';
import { AvatarBadge } from '../avatarBadge/avatarBadge';
import { AvatarFlag } from '../avatarFlag/avatarFlag';

export const Avatar = ({
  size,
  label,
  background = 'transparent',
  fallbackType = 'initials',
  styles = {},
  badgeColor,
  flagCode,
  statusBadgeIconName,
  disabled,
  ...rest
}: AvatarProperties): JSX.Element => {
  const theme = useTheme();

  const AvatarVariant: AvatarVariant = {
    initials: InitialsAvatar,
    iconDefault: IconDefaultAvatar,
    iconScreenshare: ScreenshareAvatar,
  }[fallbackType];

  const content = (
    <Box
      css={{
        background:
          background === 'transparent'
            ? 'transparent'
            : theme.palette.common.black,
        ...styles,

        '::after': disabled
          ? {
              content: '""',
              backgroundColor: theme.interactiveElement.disabled,
              position: 'absolute',
              inset: 0,
              borderRadius: theme.shape.radius100,
            }
          : undefined,
      }}
    >
      <AvatarVariant {...rest} size={size} label={label} />
    </Box>
  );

  const badge = badgeColor !== undefined && (
    <AvatarBadge
      badgeColor={badgeColor}
      statusBadgeIconName={statusBadgeIconName}
      size={size}
      disabled={disabled}
    />
  );

  const location = flagCode !== undefined && (
    <AvatarFlag flagCode={flagCode} size={size} disabled={disabled} />
  );

  return (
    <Box
      sx={{
        position: 'relative',
      }}
    >
      {Boolean(location) ? location : badge}
      {content}
    </Box>
  );
};
